import React, { useContext, useEffect, useState } from 'react';
import { DContext } from '../../../context/DataContext';
import { Link } from 'react-router-dom';
import Loading from '../../blocks/Loading';
import { BsEye } from 'react-icons/bs';
import AdminListFooter from '../../blocks/AdminListFooter';
import { useTranslation } from 'react-i18next';
import { formatContact } from '../../../utils/contact';

function Users() {
  const { t } = useTranslation();
  const { BeURL, setLoader } = useContext(DContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (BeURL) {
      setLoading(true);
      setLoader(true);
      fetch(`${BeURL}/fetch-users`, {
        credentials: "include"
      })
        .then(res => res.json())
        .then(data => {
          setLoader(false);
          setLoading(false);
          if (data.success) {
            setUsers(Array.isArray(data.users) ? data.users : []);
          } else {
            alert(t('adminCommon.requestFailed'));
          }
        })
        .catch(err => {
          setLoader(false);
          setLoading(false);
          console.log("Error in fetching users:", err);
          alert(t('adminCommon.serverError'));
        });
    }
  }, [BeURL, setLoader, t]);

  // Filter users based on search query (checks all item properties)
  const filteredUsers = users.filter(user => {
    const userString = JSON.stringify(user).toLowerCase();
    return userString.includes(searchQuery.toLowerCase());
  });

  // Sort users based on selected field and order
  if (sortField) {
    filteredUsers.sort((a, b) => {
      const valA = (a[sortField] || '').toString().toLowerCase();
      const valB = (b[sortField] || '').toString().toLowerCase();
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Pagination calculations
  const totalItems = filteredUsers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePrevious = () => {
    setCurrentPage((page) => Math.max(page - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage((page) => Math.min(page + 1, totalPages || 1));
  };

  const translateRole = (role) => {
    if (!role) return '-';
    if (role === 'admin') return t('users.roles.admin');
    if (role === 'user') return t('users.roles.user');
    return role;
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-extrabold tracking-tight text-[#1739bd]">{t('users.heading')}</h2>
      {/* Search and Sort Options */}
      <div className="grid gap-3 md:grid-cols-3">
        <div>
          <input
            type="text"
            className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
            placeholder={t('users.searchPlaceholder')}
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <div>
          <select
            className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
            value={sortField}
            onChange={e => {
              setSortField(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">{t('users.sortBy')}...</option>
            <option value="fullname">{t('users.table.fullName')}</option>
            <option value="email">{t('users.table.email')}</option>
            <option value="city">{t('users.table.city')}</option>
            <option value="country">{t('users.table.country')}</option>
            <option value="role">{t('users.table.role')}</option>
          </select>
        </div>
        <div>
          <select
            className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
            value={sortOrder}
            onChange={e => {
              setSortOrder(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="asc">{t('users.ascending')}</option>
            <option value="desc">{t('users.descending')}</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-[0_14px_34px_rgba(15,23,42,0.06)]">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-100/80">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('users.table.id')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('users.table.fullName')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('users.table.country')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('users.table.contact')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('users.table.role')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('users.table.createdAt')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">{t('users.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paginatedUsers.length > 0 ? (
              paginatedUsers.map(user => (
                <tr key={user.id}>
                  <td className="px-4 py-3 text-sm text-slate-600">{user.id}</td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{user.fullname}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{user.country}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{formatContact(user.contact)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{translateRole(user.role)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <Link to={`/admin/user/${user.id}`} className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-indigo-200 text-[#1739bd] transition hover:bg-indigo-50">
                      <BsEye />
                    </Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-sm text-slate-500">{t('users.noResults')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AdminListFooter
        totalItems={totalItems}
        currentPage={currentPage}
        totalPages={totalPages || 1}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={(value) => {
          setItemsPerPage(value);
          setCurrentPage(1);
        }}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />
    </div>
  );
}

export default Users;
